import { useMemo } from "react";

import { globalSearchConfig } from "../../../../config/search/global-search-config";
import { matchesAllTerms } from "../../../../config/search/search-utils";

import type { ProcessedEvent, FilterType, WeekFilter } from "@/types/calendar";

interface UseCalendarFiltersProps {
  events: ProcessedEvent[];
  activeFilter: FilterType;
  weekFilter: WeekFilter;
  searchQuery: string;
}

export const useCalendarFilters = ({
  events,
  activeFilter,
  weekFilter,
  searchQuery,
}: UseCalendarFiltersProps) => {
  // Helper function to format date for searching
  const formatDateForSearch = (date: Date): string => {
    return date
      .toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      })
      .toLowerCase();
  };

  // Get week date ranges
  const getWeekDates = (filter: WeekFilter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const currentDay = today.getDay();

    // Calculate the start of this week (Sunday)
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(today.getDate() - currentDay);

    // Calculate the end of this week (Saturday)
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekStart.getDate() + 6);

    switch (filter) {
      case "lastWeek":
        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(thisWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(thisWeekEnd);
        lastWeekEnd.setDate(thisWeekEnd.getDate() - 7);
        return { start: lastWeekStart, end: lastWeekEnd };

      case "nextWeek":
        const nextWeekStart = new Date(thisWeekStart);
        nextWeekStart.setDate(thisWeekStart.getDate() + 7);
        const nextWeekEnd = new Date(thisWeekEnd);
        nextWeekEnd.setDate(thisWeekEnd.getDate() + 7);
        return { start: nextWeekStart, end: nextWeekEnd };

      default: // thisWeek
        return { start: thisWeekStart, end: thisWeekEnd };
    }
  };

  // Filter system events
  const filterSystemEvents = (events: ProcessedEvent[]): ProcessedEvent[] => {
    return events.filter((event) => {
      if (!event.title || event.title === "Untitled Event") return false;
      if (!event.id) return false;
      return true;
    });
  };

  // Apply time-based filtering
  const filterByTime = (events: ProcessedEvent[]): ProcessedEvent[] => {
    const now = new Date();

    return events.filter((event) => {
      switch (activeFilter) {
        case "upcoming":
          return event.start >= now;
        case "past":
          return event.start < now;
        default:
          return true;
      }
    });
  };

  // Filter events by search query using global search config
  const filterBySearch = (events: ProcessedEvent[]): ProcessedEvent[] => {
    if (!searchQuery.trim()) return events;

    // Use global search config minimum length
    if (searchQuery.trim().length < globalSearchConfig.minSearchLength) {
      return events;
    }

    return events.filter((ev) => {
      try {
        // Use global multi-term search with all searchable fields
        const searchableFields = ["title", "description", "location"];

        // Use the global search utility for consistent multi-term matching
        if (
          matchesAllTerms(
            searchQuery,
            ev,
            searchableFields,
            globalSearchConfig.caseSensitive,
          )
        ) {
          return true;
        }

        // Additional search in formatted dates (still using old logic as it's calendar-specific)
        const query = searchQuery.toLowerCase().trim();
        const startStr = formatDateForSearch(ev.start);
        const endStr = formatDateForSearch(ev.end);
        if (startStr.includes(query) || endStr.includes(query)) return true;

        return false;
      } catch (error) {
        console.error("Error searching event:", error, ev);
        return false;
      }
    });
  };

  // Filter events by week
  const weekEvents = useMemo(() => {
    const { start, end } = getWeekDates(weekFilter);
    return filterSystemEvents(events)
      .filter((event) => {
        const eventDate = event.start;
        return eventDate >= start && eventDate <= end;
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [events, weekFilter]);

  // Apply all filters for search results
  const searchResults = useMemo(() => {
    // If there's no search query, only show results when filtering by time (not 'all')
    if (!searchQuery && activeFilter === "all") return [];

    let filtered = filterSystemEvents(events);
    filtered = filterByTime(filtered);
    filtered = filterBySearch(filtered);

    return filtered;
  }, [events, searchQuery, activeFilter]);

  // Filter events for when no search is active but time filters are applied
  const filteredEvents = useMemo(() => {
    if (searchQuery) return []; // Don't show when searching

    let filtered = filterSystemEvents(events);
    filtered = filterByTime(filtered);

    return filtered.sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [events, activeFilter, searchQuery]);

  return {
    weekEvents,
    searchResults,
    filteredEvents,
    filterSystemEvents,
    filterByTime,
    filterBySearch,
  };
};
